import {
  cardPayOnlineLabel,
  formatCents,
  formatCount,
  formatDateTime,
  paymentMethodBadge,
  qrPayLabel,
  roleLabel,
  toDateParam,
  tt,
} from '@agos/shared'

import { statusLabel } from './payment-archive'

/**
 * *Podľa rozvozárov* on *Platby online - archív* - the rules behind the tab. FRONTEND.md §9.749, on BACKEND.md
 * §10A.403 (`GET /payments/by-driver`) and §10A.402 (the trail a close now writes).
 *
 * The owner, O121: *"rozvozar Jožko bral obj 78 qr platba (úspešná) suma 20€ / 68 cardpay suma 10€ / A dole súhrn :
 * cardpay 10€ qr 20€"* - each driver's orders, what the gateway says about each, and a summary under them.
 *
 * **The server decides and this file says.** Every verdict, every flag and every figure arrives computed by
 * `DriverPayments` on the server, over the handover's own set of orders. Nothing here sums a driver's money or
 * re-derives a verdict: a second spelling of the verdict table on the client is how two screens come to disagree
 * about one order. What lives here is reading the answer, the words, the order things are drawn in, and the window.
 *
 * **No customer data.** The endpoint answers no customer name, phone or address, and the parser builds every object
 * from the keys it names, so a field the server should not have sent does not reach the screen either.
 */

// ─── The answer, as the server writes it ─────────────────────────────────────

/**
 * One group's money, in cents and counts - `DriverPayments.summarise`, over **every** order the group owns, the plain
 * cash orders the list leaves out included.
 *
 * The six handover buckets (`cashCents` to `otherCents`) add up to `revenueCents`; the `bank*` figures are what the
 * gateway holds, split by the order's method. `bucketsAddUp` holds the first statement against what arrived.
 */
export interface DriverPaymentsSummary {
  orderCount: number
  revenueCents: number
  cashCents: number
  cardCourierCents: number
  cardCourierCount: number
  cardTerminalCents: number
  /** `card_online` and the legacy `card`, which the handover takes off the cash together. */
  cardOnlineCents: number
  qrPayCents: number
  otherCents: number
  bankCardPayCents: number
  bankCardPayCount: number
  bankQrPayCents: number
  bankQrPayCount: number
  /** Paid in the gateway on an order filed as something else - the money behind `paid_not_claimed`. */
  bankElsewhereCents: number
  missingCents: number
  missingCount: number
  surplusCents: number
  surplusCount: number
  waitingCount: number
  /**
   * Orders a person changed to a card at the door or the till - `card_courier` **or** `card_terminal` - in place of
   * CardPay online or QR Pay (BACKEND.md §10A.419).
   */
  changedCount: number
  changedCents: number
  flaggedCount: number
  inTheFieldCount: number
}

/** One gateway attempt on the order - an `online_payments` row. */
export interface DriverPaymentsSession {
  paymentId: number
  /**
   * What the customer used on the bank's page - `online_payments.method`. **Not the owner's word for the order**: a
   * door QR paid by card reads `card_pay` here while the order is `qr_pay` (§10A.126, §10A.314), which is why it is
   * drawn as *kartou* / *prevodom* rather than as *CardPay* / *QR Pay*.
   */
  rail: string | null
  status: string
  amountCents: number
  createdAt: string
  paidAt: string | null
  refundedAt: string | null
  transactionId: string | null
}

/** One change of the order's method - an `order_edit_logs` row in §10A.228's shape, §10A.402. */
export interface DriverPaymentsMethodChange {
  from: string | null
  to: string | null
  byName: string | null
  /** The system role, or `system` for a mechanism. Blank when the row carries none, which old rows can. */
  byRole: string
  at: string
  /** Written by a mechanism (`system`), not by a person - §10A.402's *by a person* is the negation of this. */
  automatic: boolean
}

export interface DriverPaymentsOrder {
  orderId: number
  orderNumber: string | null
  status: string
  paymentMethod: string
  paymentStatus: string | null
  totalCents: number
  effectiveAt: string
  deliveredAt: string | null
  /** The method on the live fiscal document, or null when there is none - *bez dokladu*. */
  receiptMethod: string | null
  verdict: DriverPaymentVerdict
  flagged: boolean
  paidCents: number
  missingCents: number
  surplusCents: number
  sessions: DriverPaymentsSession[]
  methodChanges: DriverPaymentsMethodChange[]
}

export interface DriverPaymentsGroup {
  employeeId: number
  name: string
  systemRole: string
  /** A *Jazdí aj rozvoz* account, which owns its till orders as well as its deliveries. */
  alsoDrives: boolean
  summary: DriverPaymentsSummary
  /** The listed orders only - plain cash is counted in `summary` and not sent. */
  orders: DriverPaymentsOrder[]
}

/** Deliveries nobody owns - money in no handover at all (§10A.403; 44 at Nesvady, owner question #161). */
export interface DriverPaymentsUnowned {
  summary: DriverPaymentsSummary
  orders: DriverPaymentsOrder[]
}

export interface DriverPaymentsReport {
  dateFrom: string
  dateTo: string
  /** Whether the franchise's gateway is usable - Nesvady's is not, so nothing there can be checked against it. */
  gatewayActive: boolean
  drivers: DriverPaymentsGroup[]
  unownedDeliveries: DriverPaymentsUnowned
  totals: DriverPaymentsSummary
}

/**
 * The ten verdicts `DriverPayments.verdictOf` answers, in the order of §10A.403's table - §10A.419 retired `terminal`
 * (owner O128: a card taken at the door is listed only when it replaced a CardPay online or a QR Pay).
 *
 * The union below derives from this tuple, and the cross-stack test reads it out of this file, so the Java enum and
 * this list are one vocabulary held equal from both sides.
 */
export const DRIVER_PAYMENT_VERDICTS = [
  'verified',
  'overpaid',
  'short',
  'waiting',
  'refunded',
  'unpaid',
  'paid_not_claimed',
  'changed',
  'changed_to_cash',
  'attempted',
] as const

export type DriverPaymentVerdict = (typeof DRIVER_PAYMENT_VERDICTS)[number]

// ─── Reading it ──────────────────────────────────────────────────────────────

/**
 * Thrown inside the parser and caught at its door. An answer this screen cannot read in full is a failed read.
 */
class Unreadable extends Error {}

/** Cents, counts and ids are whole numbers on the wire; a decimal there is euros sent by mistake, not a figure. */
function whole(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value)) throw new Unreadable()
  return value
}

function flag(value: unknown): boolean {
  if (typeof value !== 'boolean') throw new Unreadable()
  return value
}

function word(value: unknown): string {
  if (typeof value !== 'string') throw new Unreadable()
  return value
}

/** A nullable text: blank and absent read as null, as `payment-archive.ts` reads them. */
function maybeWord(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/** A label nothing decides on: a missing one degrades a word rather than the whole answer. */
function softWord(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function list(value: unknown): readonly unknown[] {
  if (!Array.isArray(value)) throw new Unreadable()
  return value as unknown[]
}

function record(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Unreadable()
  return value as Record<string, unknown>
}

function verdictOf(value: unknown): DriverPaymentVerdict {
  const known = DRIVER_PAYMENT_VERDICTS.find((verdict) => verdict === value)
  if (known === undefined) throw new Unreadable()
  return known
}

function summaryOf(value: unknown): DriverPaymentsSummary {
  const row = record(value)
  return {
    orderCount: whole(row.orderCount),
    revenueCents: whole(row.revenueCents),
    cashCents: whole(row.cashCents),
    cardCourierCents: whole(row.cardCourierCents),
    cardCourierCount: whole(row.cardCourierCount),
    cardTerminalCents: whole(row.cardTerminalCents),
    cardOnlineCents: whole(row.cardOnlineCents),
    qrPayCents: whole(row.qrPayCents),
    otherCents: whole(row.otherCents),
    bankCardPayCents: whole(row.bankCardPayCents),
    bankCardPayCount: whole(row.bankCardPayCount),
    bankQrPayCents: whole(row.bankQrPayCents),
    bankQrPayCount: whole(row.bankQrPayCount),
    bankElsewhereCents: whole(row.bankElsewhereCents),
    missingCents: whole(row.missingCents),
    missingCount: whole(row.missingCount),
    surplusCents: whole(row.surplusCents),
    surplusCount: whole(row.surplusCount),
    waitingCount: whole(row.waitingCount),
    changedCount: whole(row.changedCount),
    changedCents: whole(row.changedCents),
    flaggedCount: whole(row.flaggedCount),
    inTheFieldCount: whole(row.inTheFieldCount),
  }
}

function sessionOf(value: unknown): DriverPaymentsSession {
  const row = record(value)
  return {
    paymentId: whole(row.paymentId),
    rail: maybeWord(row.rail),
    status: word(row.status),
    amountCents: whole(row.amountCents),
    createdAt: word(row.createdAt),
    paidAt: maybeWord(row.paidAt),
    refundedAt: maybeWord(row.refundedAt),
    transactionId: maybeWord(row.transactionId),
  }
}

function changeOf(value: unknown): DriverPaymentsMethodChange {
  const row = record(value)
  return {
    from: maybeWord(row.from),
    to: maybeWord(row.to),
    byName: maybeWord(row.byName),
    // `order_edit_logs.employee_role` is nullable, so an old row may carry none. A role is a word on a trail line,
    // not a figure or a verdict, and losing it must not turn a whole report of money into a failed read.
    byRole: softWord(row.byRole),
    at: word(row.at),
    automatic: flag(row.automatic),
  }
}

function orderOf(value: unknown): DriverPaymentsOrder {
  const row = record(value)
  return {
    orderId: whole(row.orderId),
    orderNumber: maybeWord(row.orderNumber),
    status: word(row.status),
    paymentMethod: word(row.paymentMethod),
    paymentStatus: maybeWord(row.paymentStatus),
    totalCents: whole(row.totalCents),
    effectiveAt: word(row.effectiveAt),
    deliveredAt: maybeWord(row.deliveredAt),
    receiptMethod: maybeWord(row.receiptMethod),
    verdict: verdictOf(row.verdict),
    flagged: flag(row.flagged),
    paidCents: whole(row.paidCents),
    missingCents: whole(row.missingCents),
    surplusCents: whole(row.surplusCents),
    sessions: list(row.sessions).map(sessionOf),
    methodChanges: list(row.methodChanges).map(changeOf),
  }
}

function groupOf(value: unknown): DriverPaymentsGroup {
  const row = record(value)
  return {
    employeeId: whole(row.employeeId),
    name: word(row.name),
    systemRole: word(row.systemRole),
    alsoDrives: flag(row.alsoDrives),
    summary: summaryOf(row.summary),
    orders: list(row.orders).map(orderOf),
  }
}

function unownedOf(value: unknown): DriverPaymentsUnowned {
  const row = record(value)
  return {
    summary: summaryOf(row.summary),
    orders: list(row.orders).map(orderOf),
  }
}

/**
 * Read `GET /payments/by-driver`. **A failed read is null**, and so is an answer this screen cannot read in full.
 *
 * **Strict about everything a claim rests on.** A missing money figure is not defaulted to nought: *Chýba v bráne
 * 0,00 €* in emerald is the fabricated all-clear FB-810 and FB-817 are about, so a summary the screen cannot read
 * whole is a read that failed. An unknown verdict is refused for the same reason - drawn, it would borrow a sentence
 * or draw none on a row the server may have flagged - and it can only arrive in a deploy's skew, which the
 * cross-stack test closes at build time. Dropping the one row instead would hide exactly the row that matters.
 *
 * **Lenient only about words nothing decides on**: a nullable text that is blank reads as null, and a trail row's
 * role may be blank.
 */
export function driverPaymentsOf(payload: unknown): DriverPaymentsReport | null {
  try {
    const row = record(payload)
    return {
      dateFrom: word(row.dateFrom),
      dateTo: word(row.dateTo),
      gatewayActive: flag(row.gatewayActive),
      drivers: list(row.drivers).map(groupOf),
      unownedDeliveries: unownedOf(row.unownedDeliveries),
      totals: summaryOf(row.totals),
    }
  } catch (caught) {
    if (caught instanceof Unreadable) return null
    throw caught
  }
}

// ─── Words ───────────────────────────────────────────────────────────────────


/** An order's method in the badge's vocabulary (§9.204), so the sentence and the badge beside it agree. */
function methodName(method: string | null): string {
  if (method === null || method === '') return '-'
  return paymentMethodBadge(method)?.label ?? method
}

/**
 * How the customer paid on the bank's page, as one word - §9.749's *kartou* / *prevodom*.
 *
 * **What the column really holds**, read off `TatraPayGateway.methodOf` and `V50`: `card_pay`; `qr_pay`, which is
 * both `QR_PAY` and `BANK_TRANSFER` (one route, a SEPA transfer); null when the gateway named none; and **any other
 * gateway word, lowercased** - `pay_later` and `direct_api` are in the gateway's own status table. Those are named as
 * another way rather than printed, because a raw key on a row about money reads as a fault, and filing one as a card
 * would be a claim the backend itself refuses to make.
 */
export function railLabel(rail: string | null): string {
  if (rail === 'card_pay') return tt('kartou')
  if (rail === 'qr_pay') return tt('prevodom')
  if (rail === null || rail === '') return tt('spôsob neuvedený')
  return tt('iným spôsobom')
}

/**
 * The trail row that put the order on its current method: the latest whose `to` is that method.
 *
 * Latest by its time, ties to the later row, so the answer is the server's own order (`created_at, id`) when the
 * times agree and still right if a list ever arrives unsorted.
 */
export function lastChangeInto(
  order: Pick<DriverPaymentsOrder, 'paymentMethod' | 'methodChanges'>,
): DriverPaymentsMethodChange | null {
  let found: DriverPaymentsMethodChange | null = null
  let foundAt = Number.NEGATIVE_INFINITY
  for (const change of order.methodChanges) {
    if (change.to !== order.paymentMethod) continue
    const parsed = Date.parse(change.at)
    const changedAt = Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY
    if (changedAt >= foundAt) {
      found = change
      foundAt = changedAt
    }
  }
  return found
}

/** Who made a change: the person and their role, or *automaticky* and the mechanism for `system`. */
function whoText(change: DriverPaymentsMethodChange): string {
  if (change.automatic) {
    return change.byName === null ? tt('automaticky') : `${tt('automaticky')} (${change.byName})`
  }
  const role = change.byRole === '' ? '' : roleLabel(change.byRole)
  if (change.byName === null) return role === '' ? tt('niekto z obsluhy') : role
  return role === '' ? change.byName : `${change.byName} (${role})`
}

/** One trail line: from, to, who, when. An arrow rather than *z ... na ...*, which would decline every method's name. */
export function methodChangeText(change: DriverPaymentsMethodChange): string {
  return `${methodName(change.from)} → ${methodName(change.to)} · ${whoText(change)} · ${formatDateTime(change.at)}`
}

/** The receipt's method, or that there is none. */
export function receiptText(order: Pick<DriverPaymentsOrder, 'receiptMethod'>): string {
  if (order.receiptMethod === null) return tt('bez dokladu')
  return `${tt('Doklad')}: ${methodName(order.receiptMethod)}`
}

/**
 * A verdict's badge.
 *
 * **A `switch` with no `default`**, so a twelfth verdict is TS2366 here rather than a badge borrowing another's
 * words - §9.566's reasoning, and §9.512's precedent, where removing a `default` is what caught a map app shipped
 * under the wrong label. The parser never lets a verdict this list does not name through, so nothing reaches the end.
 */
export function verdictLabel(
  order: Pick<DriverPaymentsOrder, 'verdict' | 'paymentMethod' | 'methodChanges'>,
): string {
  switch (order.verdict) {
    case 'verified':
      return tt('Overené v bráne')
    case 'overpaid':
      return tt('V bráne viac')
    case 'short':
      return tt('V bráne menej')
    case 'waiting':
      return tt('Čaká na banku')
    case 'refunded':
      return tt('Vrátená zákazníkovi')
    case 'unpaid':
      return tt('Bez platby v bráne')
    case 'paid_not_claimed':
      return `${tt('Zaplatené v bráne, vedené ako')} ${methodName(order.paymentMethod)}`
    case 'changed':
      return changedLabel(order)
    case 'changed_to_cash':
      return tt('Zmenené na hotovosť')
    case 'attempted':
      return tt('Pokus o platbu cez bránu')
  }
}

/**
 * *Zmenené z CardPay online na kartu kuriérovi* - the badge of the one card order this screen still lists, BACKEND.md
 * §10A.419, owner O128: a person put the card on the order in place of CardPay online or QR Pay, which is a claim the
 * bank should have backed that disappeared. Says **from what**, because that is the whole reason it is listed, and the
 * card in words rather than the badge's *Karta kuriér*, which the owner asked not to see here.
 */
export function changedLabel(order: Pick<DriverPaymentsOrder, 'paymentMethod' | 'methodChanges'>): string {
  const into =
    order.paymentMethod === 'card_courier'
      ? tt('na kartu kuriérovi')
      : order.paymentMethod === 'card_terminal'
        ? tt('na kartu cez terminál')
        : `${tt('na')} ${methodName(order.paymentMethod)}`
  const from = lastChangeInto(order)?.from ?? null
  if (from === null || from === '') return `${tt('Zmenené')} ${into}`
  const was =
    from === 'qr_pay' ? qrPayLabel() : from === 'card_online' || from === 'card' ? cardPayOnlineLabel() : methodName(from)
  return `${tt('Zmenené z')} ${was} ${into}`
}

/** The badge colours a verdict can take - a subset of `@agos/ui`'s `BadgeColor`. */
export type VerdictTone = 'emerald' | 'amber' | 'red' | 'zinc'

/**
 * A verdict's colour, §10A.403's tone column. Red is money the gateway does not hold; amber is a row somebody should
 * look at; grey is what the gateway cannot see. **A card taken at the door is never red** - the gateway cannot see a
 * courier terminal, so it is grey, or amber when somebody changed it - and *na kontrolu* is never *podvod*.
 */
export function verdictTone(verdict: DriverPaymentVerdict): VerdictTone {
  switch (verdict) {
    case 'verified':
      return 'emerald'
    case 'overpaid':
      return 'amber'
    case 'short':
      return 'red'
    case 'waiting':
      return 'amber'
    case 'refunded':
      return 'red'
    case 'unpaid':
      return 'red'
    case 'paid_not_claimed':
      return 'amber'
    case 'changed':
      return 'amber'
    case 'changed_to_cash':
      return 'zinc'
    case 'attempted':
      return 'zinc'
  }
}

function changeStory(change: DriverPaymentsMethodChange): string {
  return `${methodName(change.from)} → ${methodName(change.to)}: ${whoText(change)}, ${formatDateTime(change.at)}.`
}

function unpaidSentence(order: DriverPaymentsOrder): string {
  const clauses = [`${tt('Vedené ako')} ${methodName(order.paymentMethod)}`]
  clauses.push(
    order.sessions.length === 0
      ? tt('ale brána k objednávke nemá žiadnu platbu')
      : tt('ale brána nemá zaplatenú platbu'),
  )
  if (order.sessions.some((session) => session.status === 'failed')) clauses.push(tt('banka ju odmietla'))
  const change = lastChangeInto(order)
  if (change !== null && !change.automatic) {
    clauses.push(`${tt('spôsob nastavil ručne')} ${whoText(change)}`)
  }
  return `${clauses.join(', ')}. ${tt('Peniaze buď nevybral nikto, alebo ich rozvozár prevzal inak - overte to u zákazníka a opravte spôsob platby v objednávke.')}`
}

/**
 * *Zaplatené v bráne, vedené ako ...* - said by **where the order's money is filed**, because the three places fail
 * differently.
 *
 * - **Cash in a handover**: the handover asks the driver for money the bank already holds.
 * - **A card at the door or at the till** (`card_courier`, `card_terminal`): the handover takes that money off the
 *   terminal's own figure, not the order's, and the terminal never saw a payment made through the gateway - so the
 *   place it goes missing is the terminal's closure, and that is what the sentence names. Telling a driver the handover
 *   asks them for it would point at the wrong document.
 * - **Anything else**, or an order in no handover: only what is certain - the bank holds it and the order says
 *   otherwise.
 */
function paidNotClaimedSentence(order: DriverPaymentsOrder, inHandover: boolean): string {
  const method = order.paymentMethod
  if (method === 'card_courier' || method === 'card_terminal') {
    return `${tt('Banka platbu má, objednávka je však vedená ako')} ${methodName(method)} - ${tt('v uzávierke terminálu ju nenájdete. Opravte spôsob platby; ak zákazník zaplatil aj kartou pri dverách, platbu vráťte.')}`
  }
  if (method === 'cash' && inHandover) {
    return tt('Odovzdanie tržby žiada od rozvozára peniaze, ktoré má banka. Opravte spôsob platby; ak zákazník zaplatil aj inak, platbu vráťte.')
  }
  return tt('Banka platbu má, objednávka je však vedená inak. Opravte spôsob platby; ak zákazník zaplatil aj inak, platbu vráťte.')
}

function attemptedSentence(order: DriverPaymentsOrder): string {
  const tally = new Map<string, number>()
  for (const session of order.sessions) tally.set(session.status, (tally.get(session.status) ?? 0) + 1)
  const listed = [...tally].map(([status, count]) => `${formatCount(count)}× ${statusLabel(status)}`).join(', ')
  const rest = `${tt('Zaplatená platba v bráne nie je, objednávka je vedená ako')} ${methodName(order.paymentMethod)}.`
  return listed === '' ? rest : `${tt('Pokusy v bráne')}: ${listed}. ${rest}`
}

/**
 * What a verdict means for this order, said once under its badge.
 *
 * **`inHandover` is false for *Rozvoz bez rozvozára*.** Those orders are in no handover at all, so the two sentences
 * that say what the handover does with the money would be false there; the rest hold either way.
 *
 * *Bez platby v bráne* names **both** possibilities - nobody collected it, or the driver took it some other way - and
 * what to do, because the gateway cannot tell them apart and a sentence that picked one would be an accusation.
 */
export function verdictSentence(order: DriverPaymentsOrder, inHandover = true): string {
  switch (order.verdict) {
    case 'verified':
      return order.paidCents < order.totalCents
        ? `${tt('Zaplatené o')} ${formatCents(order.totalCents - order.paidCents)} ${tt('menej - v tolerancii brány.')}`
        : ''
    case 'overpaid':
      return `${tt('Brána má o')} ${formatCents(order.surplusCents)} ${tt('viac, než objednávka stojí - zvážte vrátenie rozdielu.')}`
    case 'short':
      return `${tt('Brána má')} ${formatCents(order.paidCents)}, ${tt('objednávka')} ${formatCents(order.totalCents)} (${tt('chýba')} ${formatCents(order.missingCents)}). ${tt('Ak sa objednávka po zaplatení menila, rozdiel mal rozvozár vybrať pri dverách.')}`
    case 'waiting':
      return tt('Banka platbu ešte nepotvrdila.')
    case 'refunded':
      return inHandover
        ? `${tt('Platba bola vrátená, objednávka je stále vedená ako')} ${methodName(order.paymentMethod)} - ${tt('odovzdanie tržby ju odpočíta z hotovosti.')}`
        : `${tt('Platba bola vrátená, objednávka je stále vedená ako')} ${methodName(order.paymentMethod)}.`
    case 'unpaid':
      return unpaidSentence(order)
    case 'paid_not_claimed':
      return paidNotClaimedSentence(order, inHandover)
    case 'changed': {
      // §10A.419 - a CardPay online or a QR Pay the bank holds nothing for, put on a card at the door or the till.
      const change = lastChangeInto(order)
      const terminal = tt('V bráne zaplatená nie je - porovnajte platbu s uzávierkou terminálu.')
      return change === null ? terminal : `${changeStory(change)} ${terminal}`
    }
    case 'changed_to_cash': {
      const change = lastChangeInto(order)
      return change === null ? tt('Spôsob platby bol ručne zmenený na hotovosť.') : changeStory(change)
    }
    case 'attempted':
      return attemptedSentence(order)
  }
}

// ─── The summary under a driver ──────────────────────────────────────────────

function bucketSum(summary: DriverPaymentsSummary): number {
  return (
    summary.cashCents +
    summary.cardCourierCents +
    summary.cardTerminalCents +
    summary.cardOnlineCents +
    summary.qrPayCents +
    summary.otherCents
  )
}

/**
 * Whether the six handover buckets add up to the group's revenue, which §10A.403 states as an invariant.
 *
 * Checked against what arrived rather than trusted, because a summary whose parts do not make its total is a summary
 * somebody would otherwise reconcile by hand and get wrong. **The two card buckets are still in the sum** although
 * §9.768 stops drawing them: the invariant is the server's, and a server that broke it is worth a red line whatever
 * this screen draws.
 */
export function bucketsAddUp(summary: DriverPaymentsSummary): boolean {
  return bucketSum(summary) === summary.revenueCents
}

/**
 * The money this screen's summary is about - cash, CardPay online, QR Pay and anything else - which is the revenue
 * less the two cards at the door and at the till (FRONTEND.md §9.768, owner O128: *"remove "karta kuríer" from
 * there"*). What *Spolu* says, so the total is the sum of the lines drawn above it.
 */
export function totalWithoutCardsCents(summary: DriverPaymentsSummary): number {
  return summary.revenueCents - summary.cardCourierCents - summary.cardTerminalCents
}

export type SummaryLineKey =
  | 'bank'
  | 'handover'
  | 'claimed'
  | 'missing'
  | 'surplus'
  | 'waiting'
  | 'changed'
  | 'other'
  | 'cash'
  | 'total'
  | 'mismatch'

export type SummaryTone = 'plain' | 'amber' | 'red' | 'total'

export interface SummaryLine {
  key: SummaryLineKey
  text: string
  tone: SummaryTone
}

/**
 * The summary below a driver's orders, **in the owner's order** - *CardPay online {x} · QR Pay {y}*; what the handover
 * deducts when it differs; *Chýba v bráne*; the cards that replaced CardPay online or QR Pay; *Hotovosť*; *Spolu* -
 * and a red line when the server's buckets do not add up.
 *
 * - **CardPay online and QR Pay always both**, 0,00 € included (§9.768, owner O128: *"make sure we will see both
 *   there"*). §9.741 drew QR Pay only where the report held some, and that was the gate this replaces.
 * - **No *Karta kuriér* and no *Karta - terminál* line** (owner O128). A card order is listed only when it replaced a
 *   CardPay online or a QR Pay (BACKEND.md §10A.419), and those are their own line, *Zmenené z CardPay online alebo QR
 *   Pay na kartu*. **So *Spolu* is the sum of the lines drawn** - the revenue less the two cards - and says so; the
 *   server's six buckets are still checked against its revenue, and a red line still says when they do not add up.
 * - **The handover's line** appears only when it says something the gateway's does not. **`inHandover` is false for
 *   *Rozvoz bez rozvozára***: those orders are in no handover, so the same figures are said as what the orders claim
 *   (`claimed`) - a line about a handover that will never happen would be false.
 * - **Money the gateway holds beyond the orders, and payments still waiting**, get their own lines when they exist.
 */
export function summaryLines(summary: DriverPaymentsSummary, inHandover: boolean): SummaryLine[] {
  const pair = (cardPayCents: number, qrPayCents: number): string =>
    `${cardPayOnlineLabel()} ${formatCents(cardPayCents)} · ${qrPayLabel()} ${formatCents(qrPayCents)}`
  const orders = (count: number): string => `${formatCount(count)} ${tt('obj.')}`

  const lines: SummaryLine[] = [
    {
      key: 'bank',
      text: `${tt('V bráne')}: ${pair(summary.bankCardPayCents, summary.bankQrPayCents)}`,
      tone: 'plain',
    },
  ]
  const differs =
    summary.cardOnlineCents !== summary.bankCardPayCents || summary.qrPayCents !== summary.bankQrPayCents
  if (differs) {
    lines.push(
      inHandover
        ? {
            key: 'handover',
            text: `${tt('Odovzdanie tržby odpočíta')}: ${pair(summary.cardOnlineCents, summary.qrPayCents)}`,
            tone: 'amber',
          }
        : {
            key: 'claimed',
            text: `${tt('Objednávky vedené ako')}: ${pair(summary.cardOnlineCents, summary.qrPayCents)}`,
            tone: 'amber',
          },
    )
  }
  if (summary.missingCents > 0) {
    lines.push({
      key: 'missing',
      text: `${tt('Chýba v bráne')} ${formatCents(summary.missingCents)} (${orders(summary.missingCount)})`,
      tone: 'red',
    })
  }
  if (summary.surplusCents > 0) {
    lines.push({
      key: 'surplus',
      text: `${tt('V bráne navyše')} ${formatCents(summary.surplusCents)} (${orders(summary.surplusCount)})`,
      tone: 'amber',
    })
  }
  if (summary.waitingCount > 0) {
    lines.push({
      key: 'waiting',
      text: `${tt('Čaká na banku')}: ${orders(summary.waitingCount)}`,
      tone: 'amber',
    })
  }
  if (summary.changedCount > 0) {
    lines.push({
      key: 'changed',
      text: `${tt('Zmenené z CardPay online alebo QR Pay na kartu')}: ${orders(summary.changedCount)} · ${formatCents(summary.changedCents)}`,
      tone: 'amber',
    })
  }
  if (summary.otherCents > 0) {
    lines.push({ key: 'other', text: `${tt('Iné')} ${formatCents(summary.otherCents)}`, tone: 'plain' })
  }
  lines.push({ key: 'cash', text: `${methodName('cash')} ${formatCents(summary.cashCents)}`, tone: 'plain' })
  lines.push({
    key: 'total',
    text: `${tt('Spolu bez platieb kartou cez terminál')} ${formatCents(totalWithoutCardsCents(summary))}`,
    tone: 'total',
  })
  if (!bucketsAddUp(summary)) {
    lines.push({
      key: 'mismatch',
      text: `${tt('Spôsoby platby dávajú spolu')} ${formatCents(bucketSum(summary))}, ${tt('objednávky')} ${formatCents(summary.revenueCents)} - ${tt('tento súhrn nesedí.')}`,
      tone: 'red',
    })
  }
  return lines
}

/** The card colours - a subset of `@agos/ui`'s `StatColor`, in the takings' own palette (§9.741). */
export type CardTone = 'primary' | 'violet' | 'pink' | 'emerald' | 'red'

export interface SummaryCard {
  key: 'cardpay' | 'qrpay' | 'missing'
  label: string
  value: string
  sub: string | null
  color: CardTone
}

/**
 * The cards above the drivers, over the report's `totals`.
 *
 * **`-` until the read answers, never a fabricated nought**, and *Chýba v bráne* is emerald **only on a read that
 * answered** - a green nought on a failed read is FB-810's styled all-clear. Colours follow the takings' palette:
 * online violet, QR Pay pink. **CardPay online and QR Pay always both, and no *Karta kuriér* card** (§9.768, owner
 * O128: *"remove "karta kuríer" from there ... make sure we will see both there"*).
 */
export function summaryCards(totals: DriverPaymentsSummary | null): SummaryCard[] {
  const money = (pick: (summary: DriverPaymentsSummary) => number): string =>
    totals === null ? '-' : formatCents(pick(totals))
  const orders = (pick: (summary: DriverPaymentsSummary) => number): string | null =>
    totals === null ? null : `${formatCount(pick(totals))} ${tt('obj.')}`

  return [
    {
      key: 'cardpay',
      label: `${cardPayOnlineLabel()} ${tt('v bráne')}`,
      value: money((summary) => summary.bankCardPayCents),
      sub: orders((summary) => summary.bankCardPayCount),
      color: 'violet',
    },
    {
      key: 'qrpay',
      label: `${qrPayLabel()} ${tt('v bráne')}`,
      value: money((summary) => summary.bankQrPayCents),
      sub: orders((summary) => summary.bankQrPayCount),
      color: 'pink',
    },
    {
      key: 'missing',
      label: tt('Chýba v bráne'),
      value: money((summary) => summary.missingCents),
      sub: orders((summary) => summary.missingCount),
      color: totals === null ? 'primary' : totals.missingCents > 0 ? 'red' : 'emerald',
    },
  ]
}

/** *{k} na kontrolu* in a driver's header, or nothing. *Na kontrolu* is never *podvod*. */
export function reviewLabel(flaggedCount: number): string {
  return flaggedCount > 0 ? `${formatCount(flaggedCount)} ${tt('na kontrolu')}` : ''
}

/**
 * The colour of *{n} na kontrolu* in a group's header - **red only when the gateway is missing money**, amber
 * otherwise. The three red verdicts (*V bráne menej*, *Vrátená zákazníkovi*, *Bez platby v bráne*) are exactly the
 * ones that put money in `missingCents`, so the summary answers it without reading the rows. A header shouting red over
 * a payment still waiting for the bank, or over a card somebody changed, is the *na kontrolu* that reads as
 * *podvod* this check exists not to be.
 */
export function reviewTone(summary: Pick<DriverPaymentsSummary, 'missingCents'>): 'red' | 'amber' {
  return summary.missingCents > 0 ? 'red' : 'amber'
}

/** The note while orders are still on the road, whose verdicts can still move. */
export function inTheFieldNote(count: number): string {
  return count > 0 ? `${tt('Na ceste je ešte')} ${formatCount(count)} ${tt('obj. - ich stav sa môže zmeniť.')}` : ''
}

/** The lead line, chosen by whether the franchise has a gateway to check against at all. */
export function gatewayLead(gatewayActive: boolean): string {
  // §9.768, owner O128 - the courier's card is not this screen's any more, so neither sentence names it.
  return gatewayActive
    ? tt('Platby cez bránu - CardPay online a QR Pay - sú porovnané s tým, čo odovzdanie tržby odpočíta z hotovosti. Platba kartou pri dverách sa tu ukáže, len ak nahradila CardPay online alebo QR Pay.')
    : tt('Platobná brána tu nie je zapnutá, takže sa v nej nedá nič overiť. Vidíte zmeny spôsobu platby.')
}

/**
 * Said once, always: §10A.402 writes a door change from this version on and back-fills nothing, so a missing row on
 * an older order means *unknown*, never *unchanged*.
 */
export function trailNote(): string {
  return tt('Zmenu spôsobu platby zaznamenávame až od tejto verzie - pri starších objednávkach nevieme, kto zmenil CardPay online alebo QR Pay na kartu.')
}

// ─── The window ──────────────────────────────────────────────────────────────

/** The period asked about, as the query names it - two local calendar dates, `YYYY-MM-DD`. */
export interface DriverPaymentsWindow {
  dateFrom: string
  dateTo: string
}

/** The server's widest window - `DRIVER_PAYMENTS_RANGE_TOO_WIDE` refuses anything over it (§10A.403). */
export const MAX_WINDOW_DAYS = 31

/**
 * Before this hour the default window is yesterday. A check is opened after closing time, and at half past midnight
 * *today* is a day with nothing in it yet.
 */
export const LATE_CHECK_UNTIL_HOUR = 5

function shiftDays(date: Date, days: number): Date {
  const copy = new Date(date)
  copy.setDate(copy.getDate() + days)
  return copy
}

/**
 * Today, or yesterday between 00:00 and 05:00 - §9.749.
 *
 * Local calendar dates through `toDateParam`, never `toISOString()`, which converts to UTC first and puts a
 * Bratislava midnight on the previous day (FB-171).
 */
export function defaultWindow(now: Date): DriverPaymentsWindow {
  const day = toDateParam(now.getHours() < LATE_CHECK_UNTIL_HOUR ? shiftDays(now, -1) : now)
  return { dateFrom: day, dateTo: day }
}

export const DRIVER_PAYMENT_PRESETS = ['dnes', 'vcera', '7dni', 'mesiac'] as const

export type DriverPaymentsPreset = (typeof DRIVER_PAYMENT_PRESETS)[number]

export function presetLabel(preset: DriverPaymentsPreset): string {
  switch (preset) {
    case 'dnes':
      return tt('Dnes')
    case 'vcera':
      return tt('Včera')
    case '7dni':
      return tt('7 dní')
    case 'mesiac':
      return tt('Tento mesiac')
  }
}

/**
 * What a preset means. *7 dní* is seven calendar days including today, as `report-window.ts` reads it; *Tento mesiac*
 * runs from the 1st to today. **None is wider than the server's 31 days**, so no preset button can ask a question the
 * server refuses.
 */
export function presetWindow(preset: DriverPaymentsPreset, now: Date): DriverPaymentsWindow {
  const today = toDateParam(now)
  switch (preset) {
    case 'dnes':
      return { dateFrom: today, dateTo: today }
    case 'vcera': {
      const yesterday = toDateParam(shiftDays(now, -1))
      return { dateFrom: yesterday, dateTo: yesterday }
    }
    case '7dni':
      return { dateFrom: toDateParam(shiftDays(now, -6)), dateTo: today }
    case 'mesiac':
      return { dateFrom: toDateParam(new Date(now.getFullYear(), now.getMonth(), 1)), dateTo: today }
  }
}

/** Which preset the window is, so its button can be drawn as chosen; null for a range picked by hand. */
export function activePreset(period: DriverPaymentsWindow, now: Date): DriverPaymentsPreset | null {
  for (const preset of DRIVER_PAYMENT_PRESETS) {
    const meant = presetWindow(preset, now)
    if (meant.dateFrom === period.dateFrom && meant.dateTo === period.dateTo) return preset
  }
  return null
}

/** How many days the window covers, inclusive; 0 for one that runs backwards or does not parse. */
export function windowDays(period: DriverPaymentsWindow): number {
  const from = Date.parse(`${period.dateFrom}T00:00:00Z`)
  const until = Date.parse(`${period.dateTo}T00:00:00Z`)
  if (!Number.isFinite(from) || !Number.isFinite(until) || until < from) return 0
  return Math.round((until - from) / 86_400_000) + 1
}

/**
 * The window a `?dateFrom=&dateTo=` asks for: neither is the default window, one is that one day - the server's own
 * reading of a missing bound (§10A.403). Present values pass through unchanged, because the server is what judges a
 * malformed or too-wide range, and its sentence is what the screen shows.
 */
export function windowFromSearch(
  dateFrom: string | null,
  dateTo: string | null,
  now: Date,
): DriverPaymentsWindow {
  const from = dateFrom ?? ''
  const until = dateTo ?? ''
  if (from === '' && until === '') return defaultWindow(now)
  if (from === '') return { dateFrom: until, dateTo: until }
  if (until === '') return { dateFrom: from, dateTo: from }
  return { dateFrom: from, dateTo: until }
}

// ─── Which rows, and in what order ───────────────────────────────────────────

export const DRIVER_PAYMENT_CHECKS = ['all', 'flagged', 'verified', 'outside'] as const

export type DriverPaymentsCheck = (typeof DRIVER_PAYMENT_CHECKS)[number]

export function checkLabel(check: DriverPaymentsCheck): string {
  switch (check) {
    case 'all':
      return tt('Všetko')
    case 'flagged':
      return tt('Na kontrolu')
    case 'verified':
      return tt('Overené v bráne')
    case 'outside':
      return tt('Mimo brány')
  }
}

/** The `?check=` value, or *Všetko* for anything this screen does not know - a stale link is not an error. */
export function checkOf(value: string | null): DriverPaymentsCheck {
  return DRIVER_PAYMENT_CHECKS.find((check) => check === value) ?? 'all'
}

/**
 * The methods every handover takes off the driver's cash with nothing typed - `PaymentMethods.BANK_SETTLED`, §10A.402.
 * An order on any other method is *Mimo brány*: its money did not go through the gateway, whatever the gateway saw.
 */
export const BANK_SETTLED_METHODS = ['card_online', 'card', 'qr_pay'] as const

export function isBankSettled(method: string): boolean {
  return BANK_SETTLED_METHODS.some((settled) => settled === method)
}

function matchesCheck(order: DriverPaymentsOrder, check: DriverPaymentsCheck): boolean {
  switch (check) {
    case 'all':
      return true
    case 'flagged':
      return order.flagged
    case 'verified':
      return order.verdict === 'verified'
    case 'outside':
      return !isBankSettled(order.paymentMethod)
  }
}

export function filterOrders(
  orders: readonly DriverPaymentsOrder[],
  check: DriverPaymentsCheck,
): DriverPaymentsOrder[] {
  return orders.filter((order) => matchesCheck(order, check))
}

export interface DriverPaymentsFilter {
  /** An employee id as the URL carries it, or `''` for every driver. */
  driver: string
  check: DriverPaymentsCheck
}

/**
 * The driver cards to draw, client-side: the chosen driver's alone, each with the rows the check keeps.
 *
 * **Under *Všetko* every driver stays**, a driver whose orders were all plain cash included - the list is empty and
 * the summary is the point. Under a narrowing check a driver with nothing that matches is left out, so *Na kontrolu*
 * reads as a list of what needs looking at.
 */
export function filterRows(
  groups: readonly DriverPaymentsGroup[],
  filter: DriverPaymentsFilter,
): DriverPaymentsGroup[] {
  return groups
    .filter((group) => filter.driver === '' || String(group.employeeId) === filter.driver)
    .map((group) => ({ ...group, orders: filterOrders(group.orders, filter.check) }))
    .filter((group) => filter.check === 'all' || group.orders.length > 0)
}

/**
 * *Rozvoz bez rozvozára*, last and only when it holds anything - and never while one driver is chosen, because it is
 * nobody's.
 */
export function unownedToShow(
  unowned: DriverPaymentsUnowned,
  filter: DriverPaymentsFilter,
): DriverPaymentsUnowned | null {
  if (filter.driver !== '') return null
  if (unowned.summary.orderCount === 0) return null
  const orders = filterOrders(unowned.orders, filter.check)
  if (filter.check !== 'all' && orders.length === 0) return null
  return { ...unowned, orders }
}

/** The *Rozvozár* select: everybody, then each driver by name with how many orders they had. */
export function driverOptions(
  groups: readonly DriverPaymentsGroup[],
): { value: string; label: string }[] {
  const byName = [...groups].sort((left, right) => left.name.localeCompare(right.name, 'sk'))
  return [
    { value: '', label: tt('Všetci') },
    ...byName.map((group) => ({
      value: String(group.employeeId),
      label: `${group.name} (${formatCount(group.summary.orderCount)})`,
    })),
  ]
}

/**
 * The driver cards in the order to read them: **flagged first**, then by what is missing, then by name in Slovak
 * collation - where *Č* follows *C* and *Ch* follows *H*, which an English sort gets wrong.
 */
export function sortGroups(groups: readonly DriverPaymentsGroup[]): DriverPaymentsGroup[] {
  return [...groups].sort((left, right) => {
    const flaggedFirst = Number(right.summary.flaggedCount > 0) - Number(left.summary.flaggedCount > 0)
    if (flaggedFirst !== 0) return flaggedFirst
    const missing = right.summary.missingCents - left.summary.missingCents
    if (missing !== 0) return missing
    return left.name.localeCompare(right.name, 'sk')
  })
}

function instantOf(value: string): number {
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/** A driver's rows by time, then by order id, whatever order they arrived in. */
export function ordersByTime(orders: readonly DriverPaymentsOrder[]): DriverPaymentsOrder[] {
  return [...orders].sort(
    (left, right) => instantOf(left.effectiveAt) - instantOf(right.effectiveAt) || left.orderId - right.orderId,
  )
}
